/**
 * PlanEmpleoV2 — Employee pension plan detail page (V2 redesign).
 *
 * Product sheet layout:
 * 1. Hero with plan identity + key KPIs
 * 2. Historical returns
 * 3. Costs & commissions (with sector comparison)
 * 4. Portfolio composition (ETFs / holdings)
 * 5. Documents
 * 6. Entities & legal references
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { PageHeader } from '../../../components/ui/page-header';
import { Progress } from '../../../components/ui/progress';
import ResponsiveTooltip from '../../../components/ui/responsive-tooltip';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '../../../components/ui/accordion';
import {
	Download,
	FileText,
	TrendingUp,
	PieChart,
	Loader2,
	Shield,
	Calendar,
	BarChart3,
	Percent,
	Building2,
	ExternalLink,
	ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';

import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import getPortfolioLines, { Portfolio } from '../../../api/calculators/getPortfolioLines';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { CompanyWithEppConfigI } from '../../../api/companies/interfaces/CompanyInfo';
import getCurrentUser from '../../../api/users/getCurrentUser';
import { reasonStopsContributions } from '../../../utils/rescueDocsCatalog';
import { esExEmpleado } from '../../../utils/esExEmpleado';
import useWithdrawalRequests from '../../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../../hooks/useRescuesEnabled';
import { CurrentUserI } from '../../../api/users/interfaces/Users';
import { CompanyEppConfigI } from '../../../api/companies/interfaces/CompanyEppConfig';
import { resolveEmployerContribution } from '../../../utils/resolveEmployerContribution';
import { getEmploymentPlanDocs } from '../../../constants/employmentPlanDocs';
import { CaserPlanSelected } from '../../../types/enums';

// Life cycle allocations
const LIFE_CYCLE_ALLOCATIONS = [
	{
		age: 'Menor de 35 años',
		ageRange: [0, 34],
		stocks: 80,
		bonds: 20,
		description: 'Máxima exposición a renta variable para maximizar el crecimiento.'
	},
	{
		age: 'Entre 35 y 44 años',
		ageRange: [35, 44],
		stocks: 70,
		bonds: 30,
		description: 'Alta exposición a renta variable con algo de estabilidad.'
	},
	{
		age: 'Entre 45 y 54 años',
		ageRange: [45, 54],
		stocks: 60,
		bonds: 40,
		description: 'Equilibrio entre crecimiento y preservación del capital.'
	},
	{
		age: 'Entre 55 y 64 años',
		ageRange: [55, 64],
		stocks: 50,
		bonds: 50,
		description: 'Mayor estabilidad a medida que se acerca la jubilación.'
	},
	{
		age: 'Mayor de 65 años',
		ageRange: [65, 100],
		stocks: 40,
		bonds: 60,
		description: 'Enfoque conservador para proteger el capital acumulado.'
	}
];

// Plan costs - Real public data from Caser/Indexa boletín (gestión 0,36% + depósito 0,05%)
const PLAN_COSTS = {
	ter: 0.41,
	management: 0.36,
	custody: 0.05,
	sectorAvg: 1.25
};

// TODO: Habilitar cuando tengamos API de rentabilidades reales
const SHOW_HISTORICAL_RETURNS = false;

// Historical returns - SENTINEL: replace with real data from backend
const HISTORICAL_RETURNS = [
	{ label: '2025 (YTD)', value: 77.7 },
	{ label: '2024', value: 77.7 },
	{ label: '2023', value: 77.7 },
	{ label: '2022', value: 77.7 },
	{ label: 'Desde inicio', value: 77.7 }
];

const fadeUp = (delay = 0) => ({
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.35, delay }
});

const PlanEmpleoV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId, userId } = useContext(ArcaContext);

	const [isLoading, setIsLoading] = useState(true);
	const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
	const [currentUser, setCurrentUser] = useState<CurrentUserI | null>(null);
	const [companyEppConfig, setCompanyEppConfig] = useState<CompanyEppConfigI | null>(null);

	// El plan de empleo lo contrata la EMPRESA: qué documentos y qué reparto ve el
	// partícipe depende de `caser_plan_selected`, no de cuántas carteras tenga él.
	const hasLifeCyclesPlan =
		companyEppConfig?.caserPlanSelected === CaserPlanSelected.EMPLOYMENT_LIFE_CYCLES;
	const planDocs = getEmploymentPlanDocs(companyEppConfig?.caserPlanSelected);

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			// Fetch portfolio lines
			const portfolioRes = await getPortfolioLines(token);
			setPortfolios(portfolioRes.data || []);

			// Fetch current user
			const userRes = await getCurrentUser(token);
			if (userRes.success) {
				setCurrentUser(userRes.data);
			}

			// Fetch company config
			if (companyId) {
				const companyRes = await getCompanyInfoEppConfig(token, companyId);
				// Mismo fallback que la vista de admin (`ConfigurarBeneficioAdminV2`):
				// al impersonar desde cuarteto el `companyId` de contexto no siempre
				// casa con el id real, y sin esto los documentos se degradan al plan
				// por defecto sin que nada lo indique.
				const company =
					companyRes.data?.allCompanies?.find((c: CompanyWithEppConfigI) => c.id === companyId) ||
					companyRes.data?.allCompanies?.[0];
				if (company?.companyEppConfig) {
					setCompanyEppConfig(company.companyEppConfig);
				}
			}
		} catch (error) {
			console.error('Error fetching plan data:', error);
		} finally {
			setIsLoading(false);
		}
	}, [token, companyId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Calculate user age bracket
	const userAge = currentUser?.user?.birthdate
		? new Date().getFullYear() - new Date(currentUser.user.birthdate).getFullYear()
		: null;

	const userAgeBracket =
		LIFE_CYCLE_ALLOCATIONS.find(
			(a) => userAge !== null && userAge >= a.ageRange[0] && userAge <= a.ageRange[1]
		) || LIFE_CYCLE_ALLOCATIONS[1];

	// Cost savings
	const costSaving = PLAN_COSTS.sectorAvg - PLAN_COSTS.ter;
	const costSavingPct = Math.round((costSaving / PLAN_COSTS.sectorAvg) * 100);

	// Monthly contribution (resolved via V1 priority chain)
	const monthlyContribution = resolveEmployerContribution({
		calculatedEmployerContribution: currentUser?.calculatedEmployerContribution,
		companyEppConfig,
		employeeCategory:
			currentUser?.user?.employeeCategory || currentUser?.userEppConfig?.data?.employeeCategory,
		grossAnnualSalary: currentUser?.user?.grossAnnualSalary,
		employmentStartDate: currentUser?.user?.employmentStartDate
	});

	// Plan start date
	const planStartDate = currentUser?.userEppConfig?.createdAt || companyEppConfig?.createdAt;

	// Estado de rescate de ESTE plan, desde backend. Sólo una solicitud
	// CONFIRMADA por un motivo que corta aportaciones cambia la vista: pedir un
	// rescate no es haberlo tramitado.
	const { openRequest, activeStoppingReason } = useWithdrawalRequests(token, 'EMPLOYMENT');
	// Puerta de rescates: cerrada por defecto mientras no sepamos por Caser
	// quién tiene algo rescatable.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, companyId ?? null, userId);
	const activeRequest = openRequest;

	const isRetiredBeneficiary = rescuesEnabled && reasonStopsContributions(activeStoppingReason);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="max-w-6xl mx-auto space-y-8 pb-8">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. Mismo caso que la
			    pantalla equivalente de admin: el único `h1` era el nombre del producto dentro de
			    la tarjeta, así que la pantalla no tenía título propio. Ese `h1` baja a `h2`.
			    El título sale de la MISMA clave que la etiqueta del menú. Subtítulo nuevo,
			    aprobado por Marisa el 29/09/26, espejo del de admin.
			    `width="full"` porque el ancho lo pone el contenedor (`max-w-6xl`). */}
			<motion.div {...fadeUp()}>
				<PageHeader
					width="full"
					title={translate('user.dashboard.employmentPlan')}
					subtitle={translate('employmentPlan.screenSubtitle')}
				/>
			</motion.div>

			{/* 1. HERO */}
			<motion.div {...fadeUp(0.06)}>
				<Card className="border-2 border-primary/20 overflow-hidden">
					<CardContent className="p-0">
						{/* Top bar */}
						<div className="bg-primary/5 border-b border-primary/10 px-6 py-4">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<div>
									<div className="flex items-center gap-2 mb-1">
										<h2 className="text-xl font-bold text-foreground">
											{translate('employmentPlan.title')}
										</h2>
										<Badge variant="default" className="text-[10px] uppercase tracking-wider">
											{translate('employmentPlan.indexedManagement')}
										</Badge>
									</div>
									<p className="text-sm text-muted-foreground">
										{/* El plan de 100% acciones no reparte por edad: describirlo como
										    Life Cycle es afirmar una estrategia que no es la suya. */}
										{translate(
											hasLifeCyclesPlan
												? 'employmentPlan.strategy'
												: 'employmentPlan.strategy100Actions'
										)}
									</p>
								</div>
								<Badge
									variant="outline"
									className="self-start text-xs border-primary/30 text-primary gap-1"
								>
									<Shield className="w-3 h-3" />
									{translate('employmentPlan.badge')}
								</Badge>
							</div>
						</div>

						{/* KPI strip */}
						<div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
							{[
								...(SHOW_HISTORICAL_RETURNS
									? [
											{
												label: translate('employmentPlan.kpi.ytdReturn'),
												value: `+${HISTORICAL_RETURNS[0]?.value || 0}%`,
												icon: <TrendingUp className="w-4 h-4" />,
												positive: true,
												tip: translate('employmentPlan.kpi.ytdReturnTip')
											}
										]
									: []),
								{
									label: translate('employmentPlan.kpi.ter'),
									value: `${PLAN_COSTS.ter.toFixed(2)}%`,
									icon: <Percent className="w-4 h-4" />,
									positive: true,
									tip: translate('employmentPlan.kpi.terTip')
								},
								{
									label: translate('employmentPlan.kpi.companyContributes'),
									// Beneficiaria/jubilada o ex-empleado (product#87): ya no hay aportación
									// en curso de la empresa.
									value:
										isRetiredBeneficiary || esExEmpleado(currentUser)
											? translate('rescue.planEmpleo.noLongerContributing')
											: `${Number(monthlyContribution).toFixed(2)}€/mes`,
									icon: <Building2 className="w-4 h-4" />,
									positive: true,
									tip: translate('employmentPlan.kpi.companyContributesTip')
								},
								{
									label: translate('employmentPlan.kpi.planStart'),
									value: planStartDate
										? new Date(planStartDate).toLocaleDateString('es-ES', {
												month: 'short',
												year: 'numeric'
											})
										: '—',
									icon: <Calendar className="w-4 h-4" />,
									positive: true,
									tip: translate('employmentPlan.kpi.planStartTip')
								}
							].map((kpi) => (
								<div key={kpi.label} className="p-4 text-center">
									<ResponsiveTooltip content={kpi.tip} side="bottom">
										<div className="cursor-help">
											<div className="flex items-center justify-center gap-1 mb-1">
												<span className="text-muted-foreground">{kpi.icon}</span>
												<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
													{kpi.label}
												</p>
											</div>
											<p
												className={`text-lg font-bold ${kpi.positive ? 'text-primary' : 'text-destructive'}`}
											>
												{kpi.value}
											</p>
										</div>
									</ResponsiveTooltip>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* 2. HISTORICAL RETURNS */}
			{SHOW_HISTORICAL_RETURNS && (
				<motion.div {...fadeUp(0.08)}>
					<div className="flex items-center gap-2 mb-4">
						<BarChart3 className="w-5 h-5 text-primary" />
						<h2 className="text-lg font-semibold">
							{translate('employmentPlan.historicalReturns')}
						</h2>
						<ResponsiveTooltip
							content={translate('employmentPlan.historicalReturnsDisclaimer')}
							side="top"
						/>
					</div>
					<div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
						{HISTORICAL_RETURNS.map((r) => (
							<Card key={r.label} className="border-none shadow-xs">
								<CardContent className="p-4 text-center">
									<p className="text-xs text-muted-foreground mb-1">{r.label}</p>
									<p
										className={`text-2xl font-bold tracking-tight ${r.value >= 0 ? 'text-primary' : 'text-destructive'}`}
									>
										{r.value > 0 ? '+' : ''}
										{r.value}%
									</p>
								</CardContent>
							</Card>
						))}
					</div>
					<p className="text-[11px] text-muted-foreground mt-2 italic">
						{translate('employmentPlan.returnsDisclaimer')}
					</p>
				</motion.div>
			)}

			{/* 3. COSTS & COMMISSIONS */}
			<motion.div {...fadeUp(0.14)}>
				<div className="flex items-center gap-2 mb-4">
					<Percent className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('employmentPlan.costs')}</h2>
				</div>
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('employmentPlan.managementFee')}
								</p>
								<p className="text-2xl font-bold text-foreground">
									{PLAN_COSTS.management.toFixed(2)}%
								</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('employmentPlan.annualOnAssets')}
								</p>
							</div>
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('employmentPlan.custodyFee')}
								</p>
								<p className="text-2xl font-bold text-foreground">
									{PLAN_COSTS.custody.toFixed(2)}%
								</p>
								<p className="text-[11px] text-muted-foreground">(Cecabank)</p>
							</div>
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('employmentPlan.totalCost')}
								</p>
								<p className="text-2xl font-bold text-primary">{PLAN_COSTS.ter.toFixed(2)}%</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('employmentPlan.allIncluded')}
								</p>
							</div>
						</div>

						{/* Sector comparison */}
						<div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
							<div className="flex items-center justify-between mb-3">
								<p className="text-sm font-medium text-foreground">
									{translate('employmentPlan.sectorComparison')}
								</p>
								<Badge className="bg-primary text-primary-foreground text-xs">
									{costSavingPct}% {translate('employmentPlan.cheaper')}
								</Badge>
							</div>
							<div className="space-y-3">
								<div>
									<div className="flex justify-between text-xs mb-1">
										<span className="text-primary font-medium">
											{translate('employmentPlan.yourPlan')} — {PLAN_COSTS.ter.toFixed(2)}%
										</span>
									</div>
									<Progress
										value={(PLAN_COSTS.ter / PLAN_COSTS.sectorAvg) * 100}
										className="h-2.5"
									/>
								</div>
								<div>
									<div className="flex justify-between text-xs mb-1">
										<span className="text-muted-foreground">
											{translate('employmentPlan.sectorAverage')} —{' '}
											{PLAN_COSTS.sectorAvg.toFixed(2)}%
										</span>
									</div>
									<Progress value={100} className="h-2.5 [&>div]:bg-muted-foreground/30" />
								</div>
							</div>
							<p className="text-xs text-muted-foreground mt-3">
								{translate('employmentPlan.savingsEstimate')
									.replace('{saving}', costSaving.toFixed(2))
									.replace('{amount}', String(Math.round(costSaving * 100)))}
							</p>
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* 4. PORTFOLIO COMPOSITION */}
			<motion.div {...fadeUp(0.2)}>
				<div className="flex items-center gap-2 mb-4">
					<PieChart className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('employmentPlan.portfolio')}</h2>
				</div>

				{/* Life cycle allocation for user */}
				{hasLifeCyclesPlan && (
					<Card className="border-2 border-primary/20 mb-4">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-3">
								<TrendingUp className="w-4 h-4 text-primary" />
								<p className="text-sm font-semibold">
									{translate('employmentPlan.yourAllocation')} ({userAgeBracket.age})
								</p>
							</div>
							<div className="flex items-center gap-6 mb-4">
								<div className="flex-1">
									<div className="flex justify-between text-sm mb-1">
										<span>{translate('employmentPlan.stocks')}</span>
										<span className="font-bold text-primary">{userAgeBracket.stocks}%</span>
									</div>
									<Progress value={userAgeBracket.stocks} className="h-3" />
								</div>
								<div className="flex-1">
									<div className="flex justify-between text-sm mb-1">
										<span>{translate('employmentPlan.bonds')}</span>
										<span className="font-bold">{userAgeBracket.bonds}%</span>
									</div>
									<Progress
										value={userAgeBracket.bonds}
										className="h-3 [&>div]:bg-muted-foreground/40"
									/>
								</div>
							</div>
							<p className="text-xs text-muted-foreground">{userAgeBracket.description}</p>

							{/* Age allocation table */}
							<details className="mt-4">
								<summary className="text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors flex items-center gap-1">
									<ChevronRight className="w-3 h-3" />
									{translate('employmentPlan.viewAgeTable')}
								</summary>
								<div className="mt-3 overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b">
												<th className="text-left py-2 px-3 font-medium text-muted-foreground">
													{translate('employmentPlan.age')}
												</th>
												<th className="text-center py-2 px-3 font-medium text-primary">
													{translate('employmentPlan.stocks')}
												</th>
												<th className="text-center py-2 px-3 font-medium text-muted-foreground">
													{translate('employmentPlan.bonds')}
												</th>
											</tr>
										</thead>
										<tbody>
											{LIFE_CYCLE_ALLOCATIONS.map((alloc, idx) => (
												<tr
													key={idx}
													className={`border-b last:border-0 ${alloc.age === userAgeBracket.age ? 'bg-primary/5' : ''}`}
												>
													<td className="py-2 px-3 text-sm">{alloc.age}</td>
													<td className="text-center py-2 px-3 font-medium">{alloc.stocks}%</td>
													<td className="text-center py-2 px-3 font-medium">{alloc.bonds}%</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</details>
						</CardContent>
					</Card>
				)}

				{/* Portfolio accordion */}
				<Accordion
					type={hasLifeCyclesPlan ? 'multiple' : 'single'}
					collapsible={!hasLifeCyclesPlan}
					className="w-full space-y-3"
				>
					{portfolios.map((portfolio, index) => (
						<AccordionItem
							key={portfolio.portfolioCode}
							value={portfolio.portfolioCode}
							className="border rounded-xl overflow-hidden"
						>
							<AccordionTrigger className="bg-primary text-primary-foreground px-4 py-3 hover:no-underline text-sm font-medium">
								{portfolio.portfolioCode
									.replace(/-/g, ' ')
									.replace(/\b\w/g, (c) => c.toUpperCase())}
							</AccordionTrigger>
							<AccordionContent className="p-4">
								<div className="space-y-1">
									{portfolio.lines.map((line) => (
										<div
											key={line.lineId}
											className="flex justify-between items-center py-2 border-b border-border/50 last:border-0"
										>
											<div className="flex items-center gap-2 min-w-0">
												<span className="text-sm font-medium truncate">{line.name}</span>
												<Badge variant="outline" className="text-[10px] shrink-0">
													{line.assetClass}
												</Badge>
											</div>
											<span className="text-sm font-bold text-primary shrink-0 ml-2">
												{Number(line.weight)}%
											</span>
										</div>
									))}
								</div>
							</AccordionContent>
						</AccordionItem>
					))}
				</Accordion>
			</motion.div>

			{/* 5. DOCUMENTS */}
			<motion.div {...fadeUp(0.26)}>
				<div className="flex items-center gap-2 mb-4">
					<FileText className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('employmentPlan.documents')}</h2>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{[
						{
							title: translate('employmentPlan.doc.dfp'),
							subtitle: translate('admin.planEmpleo.indexaEmpleoAcciones'),
							link: planDocs.dfpStocksUrl
						},
						{
							title: translate('employmentPlan.doc.dfp'),
							subtitle: translate('admin.planEmpleo.indexaEmpleoBonos'),
							link: planDocs.dfpBondsUrl || ''
						},
						{
							title: translate('admin.planEmpleo.managerReport'),
							subtitle: translate('admin.planEmpleo.indexaEmpleoAcciones'),
							link: planDocs.managerReportStocksUrl
						},
						{
							title: translate('admin.planEmpleo.managerReport'),
							subtitle: translate('admin.planEmpleo.indexaEmpleoBonos'),
							link: planDocs.managerReportBondsUrl || ''
						},
						{
							title: translate('employmentPlan.doc.planSpecs'),
							subtitle: translate('employmentPlan.doc.planSpecsSubtitle'),
							link: companyEppConfig?.linkRegulation || ''
						}
					]
						.filter((doc) => doc.link)
						.map((doc, idx) => (
							<Card
								key={idx}
								className="border shadow-xs hover:border-primary/30 transition-colors"
							>
								<CardContent className="p-4 flex items-center gap-4">
									<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
										<FileText className="w-5 h-5 text-primary" />
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate">{doc.title}</p>
										<p className="text-xs text-muted-foreground">{doc.subtitle}</p>
									</div>
									<a href={doc.link} target="_blank" rel="noopener noreferrer">
										<Button
											size="icon"
											variant="ghost"
											className="shrink-0 text-primary hover:bg-primary/10"
										>
											<Download className="w-4 h-4" />
										</Button>
									</a>
								</CardContent>
							</Card>
						))}
				</div>
			</motion.div>

			{/* 6. ENTITIES & LEGAL */}
			<motion.div {...fadeUp(0.32)}>
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<h2 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wider">
							{translate('employmentPlan.entities')}
						</h2>
						<div className="flex flex-wrap items-center gap-6 mb-4">
							<img
								src="/imgs/LOGO_VERDE_2.svg"
								alt="Arca"
								className="h-8 object-contain opacity-80 hover:opacity-100 transition-opacity"
							/>
							<img
								src="/imgs/caser-helvetia.png"
								alt="Caser Pensiones"
								className="h-8 object-contain opacity-80 hover:opacity-100 transition-opacity"
							/>
							<img
								src="/imgs/indexa-capital.png"
								alt="Indexa Capital"
								className="h-6 object-contain opacity-80 hover:opacity-100 transition-opacity"
							/>
							<img
								src="/imgs/cecabank-logo.png"
								alt="Cecabank"
								className="h-5 object-contain opacity-80 hover:opacity-100 transition-opacity"
							/>
						</div>
						<p className="text-sm text-muted-foreground mb-3">
							{translate('employmentPlan.entitiesDescription')}
						</p>
						<a
							href="https://dgsfp.mineco.gob.es/"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
						>
							{translate('employmentPlan.consultDGSFP')}
							<ExternalLink className="w-3 h-3" />
						</a>
					</CardContent>
				</Card>
			</motion.div>
		</div>
	);
};

export default PlanEmpleoV2;
